import React, { useCallback, useEffect } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import { Box,CircularProgress, IconButton, TableCell, TableRow } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { TableCellWithTooltip } from 'common/components/sharedCustomComponents/TableCellWithTooltip';
import { DJ_ACCOUNT_LINKING_FAILURES_DETAILS_FAILURE_TEXT, StepFunctionExecutionStatus } from 'common/constants/admin';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchStepFunctionExecutionDetails } from 'common/services/api/StepFunctions/StepFunctions.thunks';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { addRowDetails, removeRow,selectRowDetails } from '../accountLinkingDashboardSlice';
import { StepFunctionExecution } from '../AccountLinkingDashboardTypeDefs';

import { AccountLinkingDashboardRowActionsMenu } from './AccountLinkingDashboardRowActionsMenu';
import { AccountLinkingDashboardRowInnerTable } from './AccountLinkingDashboardRowInnerTable';

export function AccountLinkingDashboardRow({ row } : Readonly<{row: StepFunctionExecution}>) {
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const rowDetails = useAppSelector(selectRowDetails(row.executionId));
    const [open, setOpen] = React.useState(false);
    const [failedToGetDetails, setFailedToGetDetails] = React.useState(false);

    const fetchStepFunctionExecutionDetailsData = useCallback((executionId: string, executionType: string, signal?: AbortSignal) => {
        const requestParams = {
            signal,
            executionId,
            executionType
        };
        dispatch(fetchStepFunctionExecutionDetails(requestParams))
            .unwrap()
            .then((data) => {
                switch(data.status) {
                    case StepFunctionExecutionStatus.FAILED:
                    case StepFunctionExecutionStatus.PROCESSING:
                        dispatch(addRowDetails(data));
                        break;
                    case StepFunctionExecutionStatus.USER_IGNORED:
                    case StepFunctionExecutionStatus.SUCCEEDED:
                        dispatch(removeRow(row.executionId));
                        break;
                    default:
                        dispatch(removeRow(row.executionId));
                }
            })
            .catch((error) => {
                if (!signal?.aborted) {
                    setFailedToGetDetails(true);
                    handleListFetchError(extractStatusCode(error.message), `Step function execution details for executionId: ${row.executionId.split(':')[1]}`, enqueueSnackbar);
                }
            });
    }, []);

    const onRowClick = (event: React.MouseEvent<HTMLElement>) => {
        const elt = event.target as HTMLElement;
        if (!failedToGetDetails && (elt.tagName === 'TD' || elt.tagName === 'TH')) {
            setOpen(!open);
        }
    };

    useEffect(() => {
        const abortController = new AbortController();
        const { signal } = abortController;

        if (rowDetails === undefined) {
            fetchStepFunctionExecutionDetailsData(row.executionId, row.executionType, signal);
        }
        return () => {
            abortController.abort();
        };
    }, []);

    function getExpandColumnContents() {
        if (rowDetails !== undefined) {
            return (
                <TableCell className='no-truncate' size='small' align='center'>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <KeyboardArrowUp data-testid="row-open-indicator"/> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
            );
        } else if (failedToGetDetails) {
            return (
                <TableCell className='no-truncate' size='small' align='center'>
                    <Box paddingTop='6px'>
                        <CloseIcon color='secondary' data-testid="expand-disabled"/>
                    </Box>
                </TableCell>
            );
        } else {
            return (
                <TableCell className='no-truncate' size='small' align='center'>
                    <CircularProgress
                        size={'34px'}
                        data-testid="loading"
                    />
                </TableCell>
            );
        }
    }

    function getAppTenantColumnContents() {
        if (rowDetails !== undefined) {
            return (
                <TableCellWithTooltip
                    value={`${rowDetails?.applicationSuiteName}/${rowDetails?.tenantName}`}
                />
            );
        } else if (failedToGetDetails) {
            return (
                <TableCell>{DJ_ACCOUNT_LINKING_FAILURES_DETAILS_FAILURE_TEXT}</TableCell>
            );
        } else {
            return (
                <TableCell><CircularProgress size={'34px'}/></TableCell>
            );
        }
    }

    function getActionsMenuColumnContents() {
        if (rowDetails !== undefined) {
            if (rowDetails.status === StepFunctionExecutionStatus.PROCESSING) {
                return (
                    <TableCell className='no-truncate' data-testid='processing'>Processing...</TableCell>
                );
            } else {
                return (
                    <TableCell className='no-truncate'>
                        <AccountLinkingDashboardRowActionsMenu
                            row={row}
                            rowDetails={rowDetails}
                        />
                    </TableCell>
                );
            }
        } else if (failedToGetDetails) {
            return (
                <TableCell/>
            );
        } else {
            return (
                <TableCell><CircularProgress size={'34px'}/></TableCell>
            );
        }
    }

    return (
        <>
            <TableRow
                hover
                onClick={onRowClick}
                sx={{
                    ...(rowDetails !== undefined && {
                        cursor: 'pointer'
                    })
                }}
                data-testid={`${row.executionId.split(':')[1]}-row`}
            >
                {getExpandColumnContents()}
                <TableCellWithTooltip value={row.executionId.split(':')[1]} tableCellProps={{ component:'th', scope:'row' }}/>
                <TableCellWithTooltip value={row.startDate} isDateTime={true}/>
                <TableCellWithTooltip value={row.executionType}/>
                {getAppTenantColumnContents()}
                {getActionsMenuColumnContents()}
            </TableRow>
            {rowDetails &&
                <AccountLinkingDashboardRowInnerTable
                    open={open}
                    rowDetails={rowDetails}
                    key={`${row.executionId.split(':')[1]}-innerTable`}
                />
            }
        </>
    );
}
